import { createMemo, createSignal, type JSX, onCleanup } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";

import { useRecipesQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { closestSelector } from "@/lib/gestures/guards";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { RECIPE_SWIPE } from "@/lib/gestures/presets";

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

interface RecipeDetailCarouselProps {
  recipeId: string;
  children: JSX.Element;
}

/**
 * Wraps the recipe detail with a horizontal swipe carousel: swipe left → next
 * recipe, swipe right → previous, following the same ordering as the recipe
 * list (most-recently-updated first). The physics (axis-lock, deadzone, fling)
 * come from the shared recognizer so it matches the planner/calendar carousels;
 * a haptic fires and the panel slides on commit. `touch-action: pan-y` keeps
 * native vertical scroll, and drags that begin on a form control are ignored so
 * editing the recipe is never hijacked. The header back/save buttons stay the
 * keyboard/click path. Honors `prefers-reduced-motion` by skipping the slide.
 */
export function RecipeDetailCarousel(props: RecipeDetailCarouselProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  // Same ordering as the recipe list so swiping mirrors that surface.
  const recipes = useRecipesQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const ids = createMemo(() => (recipes.data ?? []).map((r) => r.id));
  const index = createMemo(() => ids().indexOf(props.recipeId));
  const neighborId = (delta: number): string | null => {
    const list = ids();
    const i = index();
    if (i < 0) {
      return null;
    }
    const next = i + delta;
    return next >= 0 && next < list.length ? (list[next] ?? null) : null;
  };

  const [panelRef, setPanelRef] = createSignal<HTMLDivElement>();
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);

  const panelWidth = (): number => panelRef()?.clientWidth ?? RECIPE_SWIPE.fallbackWidthPx;

  /** Animate a directional recipe change. delta>0 = next (panel slides left). */
  const animateTo = (delta: number): void => {
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: animateTo reads the neighbor at swipe time and go() navigates to that id after the transition
    const targetId = neighborId(delta);
    if (delta === 0 || !targetId) {
      // No neighbor in this direction — bounce back.
      snapBack();
      return;
    }
    const go = (): void => {
      void navigate({ to: "/recipes/$recipeId", params: { recipeId: targetId } });
    };
    if (prefersReducedMotion()) {
      setDragX(0);
      go();
      return;
    }
    const width = panelWidth();
    const outX = delta > 0 ? -width : width; // current panel leaves toward travel
    setTransitioning(true);
    setDragX(outX);
    vibrate(RECIPE_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      go();
      setDragX(0);
      setTransitioning(false);
    }, RECIPE_SWIPE.transitionMs);
  };

  const snapBack = (): void => {
    setTransitioning(true);
    setDragX(0);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: RECIPE_SWIPE.thresholdPx,
    fling: { ...RECIPE_SWIPE.fling },
    // Never start a swipe from a form control or interactive element — the
    // recipe form is fully editable, so the gesture only claims empty space.
    canStart: ({ target }) =>
      !closestSelector(
        target,
        'input, textarea, select, button, a, [contenteditable="true"], [data-no-swipe="true"]',
      ),
    onStart: () => {
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        RECIPE_SWIPE.commitThresholdPx,
        panelWidth() * RECIPE_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next recipe; swipe right (dx>0) → previous.
        animateTo(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      snapBack();
    },
  });

  const swipeHandlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  const transform = (): string => `translate3d(${dragX()}px, 0, 0)`;
  const transition = (): string =>
    transitioning() ? `transform ${RECIPE_SWIPE.transitionMs}ms ease-out` : "none";

  return (
    <div class="overflow-hidden" style={{ "touch-action": "pan-y" }} {...swipeHandlers}>
      <div
        ref={setPanelRef}
        class="will-change-transform"
        style={{ transform: transform(), transition: transition() }}
      >
        {props.children}
      </div>
    </div>
  );
}
